<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  unreadCount: { type: Number, default: 0 },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);

const resolvedUnreadCount = computed(() => {
  const value = props.unreadCount || 0;
  if (!value) return '';
  return value < 100 ? `${value}` : '99+';
});
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-1.5 py-1 rounded-lg min-w-0"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="[
      typeof icon === 'string' ? 'h-8' : 'min-h-8',
      {
        'text-n-slate-12 bg-[#6ca9f9] font-medium': isActive && !hasActiveChild,
        'text-n-slate-12 font-medium': hasActiveChild,
        'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
      },
    ]"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex-shrink-0 inline-flex items-center justify-center">
      <Icon :icon="icon" :class="typeof icon === 'string' ? 'size-4' : 'size-[1.9em]'" />
      <span
        v-if="resolvedUnreadCount && !expandable"
        class="pointer-events-none min-h-[14px] min-w-[14px] px-0.5 bg-n-ruby-9 rounded-full absolute -top-1.5 ltr:-right-2.5 rtl:-left-2.5 z-10 grid place-items-center text-[9px] leading-none text-white font-semibold"
      >
        {{ resolvedUnreadCount }}
      </span>
      <span
        v-else-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="resolvedUnreadCount && expandable"
        class="pointer-events-none min-h-[14px] min-w-[14px] px-0.5 bg-n-ruby-9 rounded-full grid place-items-center text-[9px] leading-none text-white font-semibold flex-shrink-0"
      >
        {{ resolvedUnreadCount }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
